import { jsx as _jsx } from "react/jsx-runtime";
/**
 * `<AuthProvider>` — owns the `/api/me` query and exposes the result to
 * children via `useAuth()`.
 *
 * Per FD `frontend-components.md § Providers`:
 *
 * - Calls `/api/me` on mount.
 * - Refetches on window focus (TanStack Query default — see app-level
 *   `QueryClient` config in `apps/dashboard/src/main.tsx`).
 * - Status transitions: `loading` → (`authenticated` | `anon`).
 * - Exposes `signOut` that calls `authClient.logout()` and clears the cache.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo, } from "react";
import { authClient } from "./auth-client.js";
const AuthContext = createContext(null);
export const ME_QUERY_KEY = ["auth", "me"];
export function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: ME_QUERY_KEY,
        queryFn: () => authClient.me(),
        // Keep stale data while refetching so the UI doesn't flicker on focus.
        staleTime: 30_000,
        retry: false,
    });
    const signOut = useCallback(async () => {
        try {
            await authClient.logout();
        }
        finally {
            queryClient.setQueryData(ME_QUERY_KEY, null);
            await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        }
    }, [queryClient]);
    const refresh = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
    }, [queryClient]);
    const value = useMemo(() => {
        const me = query.data ?? null;
        const status = query.isPending ? "loading" : me ? "authenticated" : "anon";
        return {
            status,
            user: me?.user ?? null,
            workspace: me?.workspace ?? null,
            role: me?.role ?? null,
            roles: me?.roles ?? [],
            permissions: me?.permissions ?? [],
            canCreateWorkspace: me?.canCreateWorkspace ?? false,
            flags: me?.flags ?? {},
            signOut,
            refresh,
        };
    }, [query.data, query.isPending, signOut, refresh]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
/**
 * Internal accessor. Throws if not wrapped in `<AuthProvider>`.
 * Exported via the `useAuth` hook in `./hooks/use-auth.ts`.
 */
export function useAuthContext() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside <AuthProvider>");
    }
    return ctx;
}
//# sourceMappingURL=auth-provider.js.map